<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { onBeforeRouteLeave, useRoute } from 'vue-router'
  import { useAppModal } from '@/components/modal'
  import { useAuthStore } from '@/stores/auth'
  import { useSearchGlobalStore } from '@/stores/search-global'
  import SearchResultListCategories from '../search-global/search-result-list-categories.vue'
  import SearchResultListOrders from '../search-global/search-result-list-orders.vue'
  import SearchResultListProducts from '../search-global/search-result-list-products.vue'
  import SearchResultListPromos from '../search-global/search-result-list-promos.vue'
  import SearchResultListUser from '../search-global/search-result-list-user.vue'
  import SearchResultListVouchers from '../search-global/search-result-list-vouchers.vue'

  const route = useRoute()
  const { modal, open, close } = useAppModal()

  defineExpose({ open })
  watch(() => route.fullPath, close)

  const auth = useAuthStore()
  const searchGlobal = useSearchGlobalStore()

  const selected = ref('Semua')
  const groups = computed(() => {
    const data: string[] = ['Semua']
    if (auth.userCan('Mengakses Menu Produk')) {
      data.push('Produk')
    }
    if (auth.userCan('Mengakses Menu Kategori Produk')) {
      data.push('Kategori')
    }
    if (auth.userCan('Mengakses Menu Promo')) {
      data.push('Promo')
    }
    if (auth.userCan('Mengakses Menu Voucher')) {
      data.push('Voucher')
    }
    if (auth.userCan('Mengakses Menu Pesanan Pelanggan')) {
      data.push('Pesanan')
    }
    if (auth.userCan('Mengakses Menu Pelanggan')) {
      data.push('Pelanggan')
    }

    return data
  })
  const totalData = computed(() => {
    switch (selected.value) {
    case 'Semua':
      return (
        searchGlobal.members.length +
        searchGlobal.products.length +
        searchGlobal.categories.length +
        searchGlobal.promos.length +
        searchGlobal.vouchers.length +
        searchGlobal.orders.length
      )
    case 'Produk':
      return searchGlobal.products.length
    case 'Kategori':
      return searchGlobal.categories.length
    case 'Promo':
      return searchGlobal.promos.length
    case 'Voucher':
      return searchGlobal.vouchers.length
    case 'Pesanan':
      return searchGlobal.orders.length
    case 'Pelanggan':
      return searchGlobal.members.length
    default:
      return 0
    }
  })

  watch(selected, () => {
    // scroll to top if needed
  })

  onBeforeRouteLeave(close)
</script>

<template>
  <app-modal
    ref="modal"
    size="medium"
    title="Pencarian">
    <app-search
      v-model="searchGlobal.search"
      v-focus />

    <div>
      <!-- filter group -->
      <div class="scrollbar-none py-3 flex gap-2 border-b lg:flex-wrap max-lg:overflow-auto">
        <button
          v-for="group in groups"
          :key="group"
          type="button"
          class="badge-navbar"
          :class="{ 'bg-primary text-white border-none': selected === group }"
          @click="selected = group">
          {{ group }}
        </button>
      </div>

      <div class="max-h-120 overflow-auto max-lg:h-120">
        <!-- search text empty -->
        <div
          v-if="searchGlobal.search === ''"
          class="h-80 flex-center p-4 text-gray-400">
          Hasil pencarian akan muncul disini
        </div>

        <!-- loading -->
        <div
          v-else-if="searchGlobal.loading"
          class="h-80 flex-center p-4 text-gray-400">
          Memuat Data..
        </div>

        <!-- empty result -->
        <div
          v-else-if="totalData === 0"
          class="h-80 flex-center p-4 text-gray-400">
          <app-empty
            title="Data tidak ditemukan"
            description="Gunakan kata kunci lain untuk menemukan data yang Anda cari."
            icon="i-far-magnifying-glass" />
        </div>

        <!-- content -->
        <div
          v-else
          class="divide-y">
          <search-result-list-products
            v-if="['Semua', 'Produk'].includes(selected)"
            :products="searchGlobal.products" />

          <search-result-list-categories
            v-if="['Semua', 'Kategori'].includes(selected)"
            :categories="searchGlobal.categories" />

          <search-result-list-promos
            v-if="['Semua', 'Promo'].includes(selected)"
            :promos="searchGlobal.promos" />

          <search-result-list-vouchers
            v-if="['Semua', 'Voucher'].includes(selected)"
            :vouchers="searchGlobal.vouchers" />

          <search-result-list-orders
            v-if="['Semua', 'Pesanan'].includes(selected)"
            :orders="searchGlobal.orders" />

          <search-result-list-user
            v-if="['Semua', 'Pelanggan'].includes(selected)"
            :users="searchGlobal.members" />
        </div>
      </div>
    </div>
  </app-modal>
</template>

<style scoped>
  @reference "@/assets/styles/main.css";
  .badge-navbar {
    @apply flex-shrink-0 rounded border px-2 py-1 text-xs;
  }
</style>
